import React, { useState, useEffect } from 'react';
import { Card, Button } from '../components/ui';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase, isConfigured } from '../supabase/supabase';

const TABLE_NAMES = [
  'trasteros_20240520',
  'clientes_20240520',
  'reservas_20240520',
  'pagos_20240520',
  'incidencias_20240520',
  'codigos_acceso_20240520'
];

const StatusRow = ({ label, ok, detail }) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
    <div className="flex items-center gap-2 text-sm">
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${ok ? 'bg-green-500' : 'bg-red-400'}`} />
      <span className="text-brand-dark font-medium">{label}</span>
    </div>
    <span className={`text-xs font-mono ${ok ? 'text-green-600' : 'text-red-500'}`}>{detail}</span>
  </div>
);

const Ajustes = () => {
  const [status, setStatus] = useState('idle');
  const [results, setResults] = useState([]);
  const [latency, setLatency] = useState(null);

  const runDiagnostic = async () => {
    setStatus('loading');
    setResults([]);
    setLatency(null);

    if (!isConfigured) {
      setStatus('error');
      setResults([{ label: 'Variables de entorno', ok: false, detail: 'VITE_SUPABASE_URL o VITE_SUPABASE_ANON_KEY no definidas' }]);
      return;
    }

    const checks = [];
    const start = performance.now();

    for (const table of TABLE_NAMES) {
      try {
        const { error, status: httpStatus } = await supabase.from(table).select('id').limit(1);
        checks.push({
          label: table,
          ok: !error,
          detail: error ? `Error: ${error.message}` : `HTTP ${httpStatus} ✓`
        });
      } catch (e) {
        checks.push({ label: table, ok: false, detail: e.message });
      }
    }

    const ms = (performance.now() - start).toFixed(0);
    setLatency(`${ms}ms`);
    setResults(checks);
    setStatus(checks.every(c => c.ok) ? 'success' : 'error');
  };

  useEffect(() => {
    runDiagnostic();
  }, []);

  const allOk = results.length > 0 && results.every(r => r.ok);

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-2xl font-bold text-brand-dark">Configuración del Sistema</h2>
          <p className="text-brand-text">Estado de la conexión y tablas en Supabase.</p>
        </div>
        <Button variant="outline" onClick={runDiagnostic} disabled={status === 'loading'} className="flex items-center gap-2">
          <SafeIcon icon={FiIcons.FiActivity} className={status === 'loading' ? 'animate-pulse' : ''} />
          Ejecutar Diagnóstico
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className={`md:col-span-2 p-6 border-t-4 ${allOk ? 'border-green-500' : status === 'error' ? 'border-brand-alert' : 'border-blue-400'}`}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-xl ${allOk ? 'bg-green-100 text-green-600' : status === 'error' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
                <SafeIcon icon={allOk ? FiIcons.FiCheckCircle : FiIcons.FiCloudOff} className="text-2xl" />
              </div>
              <div>
                <h3 className="font-bold text-brand-dark text-lg">Tablas de Supabase</h3>
                <p className="text-sm text-brand-text">
                  {status === 'loading' ? 'Verificando...' : `${results.filter(r => r.ok).length} / ${results.length} tablas accesibles`}
                </p>
              </div>
            </div>
            {latency && <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded text-gray-500">{latency}</span>}
          </div>

          {status === 'loading' ? (
            <div className="flex justify-center py-8">
              <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-brand-primary text-2xl" />
            </div>
          ) : (
            <div className="space-y-0">
              {results.map(r => <StatusRow key={r.label} label={r.label} ok={r.ok} detail={r.detail} />)}
            </div>
          )}

          {!allOk && status === 'error' && results.length > 0 && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
              <strong>Pasos para solucionar:</strong>
              <ol className="mt-1 ml-4 list-decimal space-y-1">
                <li>Ejecuta la migración SQL en el editor de Supabase.</li>
                <li>Verifica que las políticas RLS permitan acceso <code>anon</code>.</li>
                <li>Comprueba que las variables de entorno están correctamente definidas en el servidor.</li>
              </ol>
            </div>
          )}
        </Card>

        <Card className="p-6 bg-brand-dark text-white">
          <h3 className="text-sm font-bold text-brand-accent uppercase tracking-widest mb-4">Estado de Conexión</h3>
          
          <div className="space-y-4">
            <div>
              <p className="text-[10px] text-gray-400 uppercase font-black mb-1">URL de Proyecto</p>
              <p className="text-xs font-mono text-brand-light">
                {isConfigured ? '✅ Configurada' : '❌ No definida'}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 uppercase font-black mb-1">Clave de API</p>
              <p className="text-xs font-mono text-brand-light">
                {isConfigured ? '✅ Detectada' : '❌ No detectada'}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 uppercase font-black mb-1">Estado</p>
              <p className={`text-xs font-mono ${isConfigured ? 'text-green-400' : 'text-red-400'}`}>
                {isConfigured ? '✅ Configurado' : '❌ Sin configurar'}
              </p>
            </div>
            
            <div className="pt-4 border-t border-brand-primary">
              <div className="flex items-center gap-2 text-xs text-brand-accent">
                <SafeIcon icon={FiIcons.FiShield} />
                <span>Cifrado SSL Activo</span>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Ajustes;